// Admin Settings → B-roll Express (master-only tab on /admin).
//
// Global settings for the pipeline's audio:
// - ElevenLabs voice ID every B-roll voice-over is synthesized with. The
//   pipeline renders the Seedance clip SILENT and muxes this voice on — there
//   is deliberately NO fallback voice, so until this is set every render
//   fails at the voice-over step with an actionable error pointing here
//   (retry then resumes from the already-paid-for silent clip).
// - Background music (fal Stable Audio) blended under the voice-over: an
//   on/off toggle and an optional style prompt (blank = built-in default).
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";

interface BrollExpressSettingsResponse {
  elevenlabsVoiceId: string | null;
  musicEnabled: boolean;
  musicPrompt: string | null;
}

export function BrollExpressSettingsTab() {
  const { toast } = useToast();
  const [voiceId, setVoiceId] = useState("");
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [musicPrompt, setMusicPrompt] = useState("");

  const { data: settings, isLoading } = useQuery<BrollExpressSettingsResponse>({
    queryKey: ["/api/broll-express/settings"],
  });

  // Hydrate the inputs once settings arrive (and when a save round-trips).
  useEffect(() => {
    setVoiceId(settings?.elevenlabsVoiceId || "");
    setMusicEnabled(settings ? settings.musicEnabled : true);
    setMusicPrompt(settings?.musicPrompt || "");
  }, [settings?.elevenlabsVoiceId, settings?.musicEnabled, settings?.musicPrompt]);

  const saveMutation = useMutation({
    mutationFn: async (patch: Partial<BrollExpressSettingsResponse>) => {
      const res = await apiRequest("PUT", "/api/broll-express/settings", patch);
      return (await res.json()) as BrollExpressSettingsResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/broll-express/settings"] });
      toast({ title: "B-roll Express settings saved" });
    },
    onError: (e: any) => {
      toast({ title: "Failed to save", description: e?.message || String(e), variant: "destructive" });
    },
  });

  const trimmedVoice = voiceId.trim();
  const trimmedPrompt = musicPrompt.trim();
  const dirty =
    trimmedVoice !== (settings?.elevenlabsVoiceId || "") ||
    musicEnabled !== (settings ? settings.musicEnabled : true) ||
    trimmedPrompt !== (settings?.musicPrompt || "");

  return (
    <Card>
      <CardHeader>
        <CardTitle>B-roll Express</CardTitle>
        <CardDescription>
          Global audio settings. B-roll videos render silent, then the ElevenLabs voice narrates the
          approved script — with optional background music blended underneath — and both are muxed onto
          the clip. Until a voice ID is set, renders fail at the voice-over step (retry resumes without
          re-rendering).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="broll-voice-id">ElevenLabs voice ID</Label>
              <Input
                id="broll-voice-id"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                placeholder="e.g. sWsBiVcjjowceAScTnu3"
                data-testid="input-broll-voice-id"
              />
              <p className="text-xs text-muted-foreground">
                Find voice IDs in your ElevenLabs voice library. Leave empty to clear (renders will fail
                until a voice is configured).
              </p>
            </div>

            <div className="space-y-3 max-w-md">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="broll-music-enabled">Background music</Label>
                  <p className="text-xs text-muted-foreground">
                    Generate a Stable Audio track for each video, mixed quietly under the voice.
                  </p>
                </div>
                <Switch
                  id="broll-music-enabled"
                  checked={musicEnabled}
                  onCheckedChange={setMusicEnabled}
                  data-testid="switch-broll-music-enabled"
                />
              </div>
              {musicEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="broll-music-prompt">Music style prompt (optional)</Label>
                  <Textarea
                    id="broll-music-prompt"
                    value={musicPrompt}
                    onChange={(e) => setMusicPrompt(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Leave blank for the default: subtle modern instrumental, no vocals, quiet under narration."
                    data-testid="textarea-broll-music-prompt"
                  />
                </div>
              )}
            </div>

            <Button
              size="sm"
              disabled={!dirty || saveMutation.isPending}
              onClick={() =>
                saveMutation.mutate({
                  elevenlabsVoiceId: trimmedVoice || null,
                  musicEnabled,
                  musicPrompt: trimmedPrompt || null,
                })
              }
              data-testid="button-save-broll-settings"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
